import { Box, Flex } from "@chakra-ui/react";
import DateBox from "./DateBox";
import { isSameDay } from "../../pages/user/NotificationEvents";
import { useEffect, useRef, useState } from "react";


interface DatePickerProps {
    date: Date | null;
    setDate: React.Dispatch<React.SetStateAction<Date | null>>;
    datesWithEventsSet?: Set<string>; // Updated type
}

const generateDateArray = (numDates: number): Date[] => {
    const today = new Date();
    today.setDate(today.getDate());

    // Calculate 9 months from today
    const maxDate = new Date(today);
    maxDate.setMonth(maxDate.getMonth() + 9);

    // Generate dates up to 9 months forward or the specified number of dates, whichever is smaller
    const actualNumDates = Math.min(numDates, Math.ceil((maxDate.getTime() - today.getTime()) / (24 * 60 * 60 * 1000)));

    return Array.from({ length: actualNumDates }, (_, i) => new Date(today.getTime() + i * 24 * 60 * 60 * 1000));
};

function daysBetween(date: Date | string): number {
    const now = new Date();
    const givenDate = new Date(date);
  
    // Normalize the dates to midnight to avoid partial day differences
    const nowNormalized = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const givenDateNormalized = new Date(givenDate.getFullYear(), givenDate.getMonth(), givenDate.getDate());

    // Calculate the time difference in milliseconds
    const timeDifference = givenDateNormalized.getTime() - nowNormalized.getTime();

    // Convert time difference from milliseconds to days
    const millisecondsPerDay = 1000 * 60 * 60 * 24;
    const dayDifference = timeDifference / millisecondsPerDay;

    return dayDifference;
}

function formatDateForComparison(date: Date): string {
    // Format the date as 'YYYY-MM-DD' using local timezone components.
    // IMPORTANT: Do NOT use toISOString() here — it converts to UTC, which shifts
    // the date forward by 1 day for users west of UTC (e.g. LA at 5pm PST = next day in UTC).
    // The backend returns event dates in the venue's local timezone, so we must compare
    // using local date components to match correctly.
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

const DatePickerSwipe = (props: DatePickerProps) => {
    const { datesWithEventsSet = new Set<string>() } = props; // Default to empty Set

    const scrollRef = useRef<HTMLDivElement>(null);
    const boxRef = useRef<HTMLDivElement>(null);
    const [boxWidth, setBoxWidth] = useState<number>(0);

    useEffect(() => {
        const handleResize = () => {
            if (boxRef.current) {
                const newWidth = boxRef.current.offsetWidth;
                setBoxWidth(newWidth);
            }
        };

        handleResize();

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const dateBoxWidth = 28 + 24; // Adjust as needed

    useEffect(() => {
        if (props.date == null) return;

        const container = scrollRef.current;

        const dateBoxGap = (props.date != null) ? daysBetween(props.date) : 0;
        const boxesPerPage = Math.ceil(boxWidth / dateBoxWidth);

        const previousGap = Math.floor((container?.scrollLeft ?? 0) / dateBoxWidth);

        console.log("dateBoxGap", dateBoxGap);
        console.log("boxesPerPage", boxesPerPage);
        console.log("previousGap", container?.scrollLeft);

        if (container && (previousGap == null || dateBoxGap <= previousGap - 1 || dateBoxGap >= previousGap + boxesPerPage + 1)) {
            container.scrollLeft = dateBoxGap * dateBoxWidth; // Force scroll position
        }
    }, [props.date, boxWidth]);

    return (
        <Box w='100%' overflowY='hidden' pb='10px' ref={boxRef}>
            <Flex 
                justifyContent='space-between'
                alignItems='center' 
                gap='4px'
                overflowX='scroll'
                ref={scrollRef}
                sx={{
                    "&::-webkit-scrollbar": {
                        display: "none",
                    },
                    "&": {
                        scrollbarWidth: "none",
                    },
                }}
            >
                {generateDateArray(270).map((date: Date, i: number) => {
                    return (
                        <DateBox 
                            date={date} 
                            onClick={() =>
                                isSameDay(date, props.date) ? props.setDate(null) : props.setDate(date)
                            }
                            key={i}
                            activeDate={props.date}
                            hasEvent={datesWithEventsSet.has(formatDateForComparison(date))}
                        />
                    )
                })}
            </Flex>
        </Box>
    )
}

export default DatePickerSwipe;